/**
 * A repeatable set of rows, collected before the thing they belong to exists.
 *
 * Three of the client form's sections are the same shape — escalation points,
 * addresses, GST registrations — and each is a list of small records written
 * after the client has an id. Writing that editor three times would be three
 * chances to make the same small mistake differently, so it is written once
 * and driven by a field spec.
 *
 * Its own state per row, deliberately: adding a row clears these boxes without
 * touching the client form around them, and Enter adds a row rather than
 * submitting the whole dialog — which is what a nested form would otherwise
 * do.
 *
 * Not applied to the interview points beside it. That editor works, is covered,
 * and rewriting it to prove a point about reuse would be churn on the one
 * section nobody asked to change.
 */
import { useState, type ReactNode } from 'react';
import { X } from 'lucide-react';

export interface StagedField {
  key: string;
  label: string;
  placeholder?: string;
  /** The one field that must be filled for the row to be worth keeping. */
  required?: boolean;
  maxLength?: number;
  inputMode?: 'text' | 'numeric' | 'decimal' | 'email';
  type?: string;
  /** Half width by default; a long address wants the whole row. */
  wide?: boolean;
  /**
   * Space the characters out.
   *
   * For a code read one character at a time against a document rather than as
   * a word — a GSTIN is the case it exists for. Not a general class hook:
   * that would let any caller restyle a shared control into something that no
   * longer matches its neighbours.
   */
  spaced?: boolean;
}

export type StagedRow = Record<string, string | null>;

export default function StagedList({
  legend,
  hint,
  addLabel,
  fields,
  rows,
  onChange,
  summary,
  header,
}: {
  legend: string;
  hint?: string;
  addLabel: string;
  /**
   * Something that belongs to the section but not to each row.
   *
   * Rendered at the top of the fieldset, above the staged list. The client's
   * registered name is the case it exists for: it is read off the same
   * certificate as the GSTINs below it and belongs in the same box, but there
   * is one of it per client rather than one per registration — so it cannot be
   * a `field`, which would put a copy on every row.
   */
  header?: ReactNode;
  fields: StagedField[];
  rows: StagedRow[];
  onChange: (rows: StagedRow[]) => void;
  /** How one staged row reads back in the list above the inputs. */
  summary: (row: StagedRow) => string;
}) {
  const [draft, setDraft] = useState<Record<string, string>>({});

  const requiredKey = fields.find((f) => f.required)?.key ?? fields[0].key;

  function add() {
    // Only the required field is checked, matching the API. A row with one
    // useful value on it is still worth recording, and refusing it until
    // every box is full is how the record goes unwritten entirely.
    if (!(draft[requiredKey] ?? '').trim()) return;
    const row: StagedRow = {};
    for (const field of fields) {
      row[field.key] = (draft[field.key] ?? '').trim() || null;
    }
    onChange([...rows, row]);
    setDraft({});
  }

  return (
    <fieldset className="rounded-lg border border-gray-200 p-3">
      <legend className="px-1 text-xs font-medium text-gray-600">{legend}</legend>

      {header && <div className="mb-3">{header}</div>}

      {rows.length > 0 && (
        <ul className="mb-2 space-y-1">
          {rows.map((row, index) => (
            <li
              key={`${row[requiredKey]}-${index}`}
              className="flex items-center justify-between gap-2 rounded bg-gray-50 px-2 py-1 text-xs"
            >
              <span className="min-w-0 truncate text-gray-700">{summary(row)}</span>
              <button
                type="button"
                className="shrink-0 text-gray-400 transition-colors hover:text-red-600"
                aria-label={`Remove ${row[requiredKey] ?? 'row'}`}
                onClick={() => onChange(rows.filter((_, i) => i !== index))}
              >
                <X className="h-3 w-3" />
              </button>
            </li>
          ))}
        </ul>
      )}

      <div className="grid grid-cols-2 gap-2">
        {fields.map((field) => (
          <label
            key={field.key}
            className={`block text-xs text-gray-600 ${field.wide ? 'col-span-2' : ''}`}
          >
            {field.label}
            <input
              spellCheck={false}
              className={`input mt-1 ${field.spaced ? 'tracking-[0.12em]' : ''}`}
              type={field.type}
              inputMode={field.inputMode}
              placeholder={field.placeholder}
              maxLength={field.maxLength}
              value={draft[field.key] ?? ''}
              onChange={(e) =>
                setDraft({ ...draft, [field.key]: e.target.value })
              }
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  add();
                }
              }}
            />
          </label>
        ))}
      </div>

      <button
        type="button"
        className="btn-secondary mt-2 text-xs"
        onClick={add}
      >
        {addLabel}
      </button>

      {hint && <p className="mt-2 text-[11px] text-gray-500">{hint}</p>}
    </fieldset>
  );
}
